<template>
  <div v-if="fetching">
    <BaseSpinner
      height="40px"
      width="40px"
      v-if="true"
    />
  </div>
  <form
    @submit.prevent="submitForm"
    class="row"
    v-else
  >
    <div class="col-xs-8">
      <div class="row">
        <div class="col-xs-12 update-close">
          <p @click.prevent="$emit('close')">
            ANNULLER <font-awesome-icon icon="times" />
          </p>
        </div>

        <section class="col-xs-12">
          <Box>
            <BaseInput
              label="ID / navn"
              v-model="info.name"
              :disabled="disableNameInput"
            />

            <BaseFormField label="Publiceringsdato">
              <Datejump v-model="info.publishDate">
                <BaseDatetimePicker v-model="info.publishDate" />
              </Datejump>
            </BaseFormField>

            <BaseFormField label="Status">
              <BaseCheckbox
                label="Slettet"
                v-model="info.deleted"
              />
            </BaseFormField>

            <BaseCheckbox
              label="Udsend notifikation"
              v-model="info.sendNotification"
            />

            <BaseTextarea
              label="Intern note"
              v-model="info.internalNote"
            />

            <BaseFormField label="Varenumre">
              <BaseSelect
                v-model="productNumbers"
                placeholder="Tilføj varenumre"
                label="productNumber"
                class="default"
                track-by="productNumber"
                tag-position="bottom"
                :options="[]"
                :multiple="true"
                :taggable="true"
                @tag="addProductNumber"
              />
            </BaseFormField>
          </Box>

          <Box>
            <RouteOfAdministrationPicker :list="routeOfAdministrations" />
          </Box>

          <Box>
            <h3>Indholdsstoffer</h3>
            <BaseSelect
              v-model="selectedSubstances"
              placeholder="Søg efter indholdsstoffer"
              label="name"
              class="default"
              track-by="id"
              tag-position="bottom"
              :options="activeSubstancesList"
              :multiple="true"
              :taggable="false"
              @select="onToggleSelectSubstance"
            />
          </Box>

          <Box>
            <GlobalNotesSelector
              :notes="filteredGlobalNotes"
              :current-date="info.publishDate"
              @input="onToggleNote"
            />
          </Box>

          <Box>
            <LocalNotes
              :notes="filteredLocalNotes"
              @add="localNotes.push($event)"
              @remove="onRemoveLocalNote"
            />
          </Box>
        </section>
        <div class="col-xs-12">
          <BaseButton
            label="Gem præparat"
            :disabled="loading"
            full
          />
          <BaseSpinner
            height="40px"
            width="40px"
            v-if="loading"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import BaseDatetimePicker from '../../BaseDatetimePicker.vue';
import Datejump from '../../Datejump.vue';
import BaseSelect from '../../BaseSelect.vue';
import RouteOfAdministrationPicker from '../../RouteOfAdministrationPicker.vue';
import LocalNotes from '../../LocalNotes.vue';
import GlobalNotesSelector from '../../GlobalNotesSelector.vue';
import BaseCheckbox from '../../BaseCheckbox.vue';
import Box from '../../Box.vue';
import BaseSpinner from '../../BaseSpinner.vue';
import BaseButton from '../../BaseButton.vue';
import BaseTextarea from '../../BaseTextarea.vue';
import BaseFormField from '../../BaseFormField.vue';
import BaseInput from '../../BaseInput.vue';
import PreparationService from '../../../services/PreparationService';
import CreateUpdatePreparation from '../../../mixins/CreateUpdatePreparation';

export default {
    name: 'UpdatePreparationForm',
    mixins: [CreateUpdatePreparation],
    props: {
        form: { type: Object, required: true },
        fromImport: { type: Boolean, default: false },
        meta: { type: Object, default: () => {} },
        action: {
            type: String,
            required: true,
            validator(value) {
                return ['update', 'newVersion'].indexOf(value) !== -1;
            },
        },
    },
    components: {
        BaseInput,
        BaseFormField,
        BaseTextarea,
        BaseButton,
        BaseSpinner,
        Box,
        BaseCheckbox,
        GlobalNotesSelector,
        LocalNotes,
        RouteOfAdministrationPicker,
        BaseSelect,
        Datejump,
        BaseDatetimePicker,
    },
    data() {
        return {
            fetching: true,
        };
    },
    computed: {
        disableNameInput() {
            if (this.action === 'newVersion') {
                return true;
            }

            if ('versionNumber' in this.info) {
                if (this.info.versionNumber > 1) {
                    return true;
                }

                return window.moment(this.info.publishDate).isSameOrBefore(window.moment()) && this.info.versionNumber === 1;
            }

            return false;
        },
    },
    methods: {
        async submitForm() {
            // validate
            if (!this.requestData.info.name) {
                this.$snotify.error('Navn skal udfyldes');
                return;
            }
            try {
                this.loading = true;
                let action = 'update';

                if (this.action === 'newVersion') {
                    action = 'newVersion';
                }

                const format = { ...this.requestData };
                if (action === 'newVersion') {
                    delete format.info.versionNumber;
                }
                const payload = await PreparationService[action](format);

                this.$snotify.success(`${payload.info.name} er gemt`);
                this.loading = false;
                this.$emit('saved', { payload, action: this.action });
            } catch (error) {
                this.loading = false;
            }
        },
    },
    async mounted() {
        try {
            this.fetching = true;

            await this.loadGlobalNotes();
            await this.loadRouteOfAdministration();
            await this.loadSubstances();

            this.info = { ...this.form.info };
            this.localNotes = this.form.localNotes.filter(note => !note.isInherited);
            this.productNumbers = [...this.form.productNumbers];

            this.routeOfAdministrations = this.getAllRouteOfAdministration.map(route => ({ ...route, selected: this.form.routeOfAdministrations.includes(route.id) }));

            this.$nextTick(() => {
                const selected = this.activeSubstancesList.filter(substance => this.form.substances.includes(substance.id));
                this.selectedSubstances = [...this.selectedSubstances, ...selected];

                this.refreshSelectedSubstances();
            });

            if (this.action === 'newVersion') {
                this.info.internalNote = '';
                this.info.sendNotification = true;

                const defaultDate = window
                    .moment()
                    .add(1, 'day')
                    .startOf('day')
                    .format();
                if (!this.fromImport) {
                    this.info.publishDate = defaultDate;
                } else {
                    this.info.publishDate = window.moment(this.info.publishDate) >= window.moment() ? this.info.publishDate : defaultDate;
                }
            }

            this.selectedGlobalNotes = this.form.globalNotes.filter(note => !note.isInherited).map(note => note.id);

            this.initGlobalNotes();
            this.fetching = false;
        } catch (error) {
            //
        }
    },
    watch: {
        'info.publishDate': function temp() {
            this.initGlobalNotes();
            this.refreshSelectedSubstances();
        },
    },
};
</script>

<style lang="scss" scoped>
.update-close {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
    user-select: none;
}
</style>
